import { stringify } from 'flatted';
import api from './api';

type LogType = 'log' | 'error' | 'warn' | 'info' | 'debug';

interface LogEntry {
  type: LogType;
  message: string;
  date: number;
}

class LogCollector {
  private sendingLogs: LogEntry[] = [];
  private logs: LogEntry[] = [];
  private originalMethods: { [key in LogType]?: Function } = {};
  private readonly sessionId: string;
  private isSending: boolean = false;
  private token: string = '';

  constructor(
    sessionId: string,
  ) {
    this.sessionId = sessionId;
    this.init();
  }

  private async init() {
    this.overrideConsoleMethods();
    this.attachUnloadEvent();
    this.token = await api.getToken();
  }

  private overrideConsoleMethods() {
    const methods: LogType[] = ['log', 'error', 'warn', 'info', 'debug'];

    methods.forEach(method => {
      this.originalMethods[method] = console[method].bind(console);
      console[method] = (...args: any[]) => {
        const log = this.convertToString(args);
        this.addLog(method, log);
        this.originalMethods[method]?.(...args);
      };
    });
  }

  private attachUnloadEvent() {
    window.addEventListener('pagehide', () => this.sendLogs());
  }

  public addLog(type: LogType, message: string) {
    this.logs.push({ type, message, date: new Date().getTime() });
  }

  public sendLogs() {
    if (this.isSending || !this.logs.length) return;

    this.isSending = true;
    this.sendingLogs = [...this.sendingLogs, ...this.logs];
    this.logs = [];

    const data = JSON.stringify({
      sessionId: this.sessionId,
      logs: this.sendingLogs,
    });

    api.sendLogs(this.token, data).then(() => {
      this.isSending = false;
      this.sendingLogs = [];
    }).catch(() => {
      this.isSending = false;
    });
  }

  public restoreConsoleMethods() {
    Object.entries(this.originalMethods).forEach(([method, originalMethod]) => {
      if (originalMethod) {
        console[method as keyof Console] = originalMethod as any;
      }
    });
  }

  private convertToString(args: any[]): string {
    const modifiedArgs = args.map(arg => {
      if (typeof arg === 'object' && arg !== null) {
        return stringify(arg);
      }
      return String(arg);
    });
    const filteredArgs = modifiedArgs.filter(arg => !arg.startsWith("color:"));

    return filteredArgs.join(' ').replace(/%s|%o|%c/g, '');
  }
}

export default LogCollector;